import styles from './CourseContent.module.css';
import CourseQuiz from "../course-quiz/CourseQuiz.tsx";
import CodeBlock from "../code-block/CodeBlock.tsx";
import {useState, useEffect} from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../store/store';
import { Link } from 'react-router-dom';

function CourseContent({lesson, onComplete, isEnrolled, onEnroll}) {
    const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);

    if (!lesson) return null;

    const content = lesson.content;  // destructure content

    return (
        <article className={styles.content}>

            {/* ── Title ── */}
            <h1 className={styles.title}>{content.title}</h1>

            {/* ── Divider ── */}
            <div className={styles.titleRule}/>

            {/* ── Intro ── */}
            <p className={styles.intro}>{content.intro}</p>

            {/* ── Sections ── */}
            {content.sections.map((sec, i) => (
                <section key={i} className={styles.section}>
                    <h2 className={styles.sectionHeading}>{sec.heading}</h2>

                    {sec.body.split('\n').map((para, j) =>
                        para.trim() ? (
                            <p key={j} className={styles.body}>{para}</p>
                        ) : null
                    )}

                    {sec.tip && (
                        <div className={styles.callout + ' ' + styles.calloutTip}>
                            <span className={styles.calloutIcon}>💡</span>
                            <p>{sec.tip}</p>
                        </div>
                    )}

                    {sec.warning && (
                        <div className={styles.callout + ' ' + styles.calloutWarning}>
                            <span className={styles.calloutIcon}>⚠️</span>
                            <p>{sec.warning}</p>
                        </div>
                    )}

                    {sec.code && <CodeBlock lang={sec.code.lang} snippet={sec.code.snippet}/>}
                </section>
            ))}

            {/* ── Quiz ── */}
            {content.quiz && (
                isLoggedIn
                    ? <CourseQuiz quiz={content.quiz}/>
                    : (
                        <div className={styles.lockedBanner}>
                            <div className={styles.lockedIcon}>
                                <svg width="22" height="22" viewBox="0 0 16 16" fill="none">
                                    <rect x="3" y="7" width="10" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                                    <path d="M5 7V5a3 3 0 0 1 6 0v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                </svg>
                            </div>
                            <h3 className={styles.lockedTitle}>Unlock quizzes & progress tracking</h3>
                            <p className={styles.lockedText}>Create a 100% free account to test your knowledge and track your learning journey.</p>
                            <div className={styles.lockedActions}>
                                <Link to="/registration" className={styles.lockedBtn}>Sign up for free →</Link>
                                <Link to="/login" className={styles.lockedBtnSecondary}>Log in</Link>
                            </div>
                        </div>
                    )
            )}

            {/* ── Completion ── */}
            {isLoggedIn && !isEnrolled && (
                <div className={styles.completion}>
                    <p className={styles.completionText}>Enjoying this course?</p>
                    <button onClick={onEnroll} className={styles.completionBtn}>
                        Enroll to track your progress
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M7 2L12 7L7 12M2 7H12" stroke="currentColor" strokeWidth="1.8"
                                  strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>
            )}

            {isLoggedIn && isEnrolled && (
                <div className={styles.completion}>
                    <p className={styles.completionText}>Finished this lesson?</p>
                    <button onClick={onComplete} className={styles.completionBtn}>
                        Mark as complete
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M2.5 7L5.5 10L11.5 4" stroke="currentColor" strokeWidth="1.8"
                                  strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>
            )}

        </article>
    );
}

export default CourseContent;